import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import type { ErpModule } from '@/api/platform.api';
import { resolveModuleFromPath } from '@/config/appNavigation';
import { isPlatformAdminRoute } from '@/config/moduleDefaults';
import { isGlobalWorkspaceRoute } from '@/constants/globalWorkspaceRoutes';
import { isStaleModulePath } from '@/utils/moduleNavigation';
import { getModuleEntryByErpModule } from '@/config/moduleEntries';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import { isOrgOwner } from '@/utils/moduleAccess';
import { setWorkspaceModule } from '@/store/authSlice';

/**
 * Active application for sidebar and app launcher.
 * Org owners: workspaceModule (switch without re-login). Scoped users: JWT entryModule.
 */
export function useActiveModule(): ErpModule | null {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const workspaceModule = useAppSelector((s) => s.auth.workspaceModule);
  const entryModule = useAppSelector((s) => s.auth.entryModule);
  const enabledModules = useAppSelector((s) => s.auth.enabledModules);
  const authLoading = useAppSelector((s) => s.auth.loading);
  const permissions = useAppSelector((s) => s.auth.user?.permissions ?? []);
  const roleCode = useAppSelector((s) => s.auth.user?.roleCode);
  const owner = isOrgOwner(permissions, roleCode);

  const inferred = useMemo(
    () => resolveModuleFromPath(location.pathname),
    [location.pathname],
  );

  const fallback = useMemo(() => {
    for (const code of enabledModules) {
      if (getModuleEntryByErpModule(code)) return code as ErpModule;
    }
    return 'POS' as ErpModule;
  }, [enabledModules]);

  const activeModule = owner
    ? (workspaceModule ?? inferred ?? fallback)
    : (entryModule ?? workspaceModule ?? inferred ?? fallback);

  useEffect(() => {
    if (!owner) return;
    if (authLoading) return;
    if (isPlatformAdminRoute(location.pathname)) return;
    if (isGlobalWorkspaceRoute(location.pathname)) return;
    if (workspaceModule && isStaleModulePath(location.pathname, workspaceModule)) return;

    const resolved = inferred ?? (!workspaceModule ? fallback : null);
    if (!resolved || workspaceModule === resolved) return;
    dispatch(setWorkspaceModule(resolved));
  }, [owner, authLoading, workspaceModule, inferred, fallback, dispatch, location.pathname]);

  return activeModule;
}
